import { AlertTriangle } from 'lucide-react';
import { useId, useState } from 'react';
import {
  MAX_NON_CONFORMITY_SOLUTION,
  MIN_NON_CONFORMITY_SOLUTION,
  type NonConformity,
} from '@obraplan/core/domain/quality/non-conformity';
import { describePlaces } from '@obraplan/core/domain/quality/non-conformity-place';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { formatShortDate } from '@/lib/format';

/**
 * O aviso da remedição (Fase 37, D10 — RN-522): a frente × serviço que se mede de novo tem NC
 * aberta de outro dia.
 *
 * Medir de novo é a hora de ver se o retrabalho foi feito. O aviso lista as NCs abertas e, para
 * quem trata NC, oferece encerrar cada uma ali, com a solução — a mesma regra da aba (RN-482).
 * O retrabalho entra na produção pela medição deste dia (RN-477); encerrar não mexe em número.
 */
export function PendingNonConformities({
  nonConformities,
  serviceName,
  unitName,
  disabled,
  onResolve,
}: {
  readonly nonConformities: readonly NonConformity[];
  readonly serviceName: (serviceId: string) => string | undefined;
  readonly unitName?: ((serviceId: string) => string | undefined) | undefined;
  readonly disabled: boolean;
  /** Ausente: quem mede não trata NC, e o aviso fica sem o botão. */
  readonly onResolve?: ((nc: NonConformity, solution: string) => Promise<boolean>) | undefined;
}) {
  if (nonConformities.length === 0) return null;
  const uma = nonConformities.length === 1;
  return (
    <section
      aria-label="Não conformidades abertas desta frente"
      className="flex flex-col gap-3 rounded-md border border-(--color-state-warn) bg-(--color-surface) p-3"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-(--color-state-warn)"
        />
        <div className="flex flex-col gap-0.5">
          <h4 className="text-sm font-medium">
            {uma
              ? 'Esta frente tem uma não conformidade aberta'
              : `Esta frente tem ${String(nonConformities.length)} não conformidades abertas`}
          </h4>
          <p className="text-xs text-(--color-content-muted)">
            Se o retrabalho foi feito, meça-o neste dia e encerre a não conformidade com a solução.
            {onResolve === undefined && ' Quem trata não conformidades encerra na aba delas.'}
          </p>
        </div>
      </div>
      <ul className="flex flex-col gap-2">
        {nonConformities.map((nc) => (
          <PendingItem
            key={nc.id}
            nc={nc}
            serviceName={serviceName(nc.serviceId)}
            unitName={unitName?.(nc.serviceId)}
            disabled={disabled}
            onResolve={onResolve}
          />
        ))}
      </ul>
    </section>
  );
}

function PendingItem({
  nc,
  serviceName,
  unitName,
  disabled,
  onResolve,
}: {
  readonly nc: NonConformity;
  readonly serviceName: string | undefined;
  readonly unitName: string | undefined;
  readonly disabled: boolean;
  readonly onResolve?: ((nc: NonConformity, solution: string) => Promise<boolean>) | undefined;
}) {
  const id = useId();
  const [aberto, setAberto] = useState(false);
  const [solution, setSolution] = useState('');
  const [gravando, setGravando] = useState(false);
  const lugares = describePlaces(nc.places);
  const detalhes = [
    formatShortDate(nc.date),
    serviceName,
    nc.quantity === undefined
      ? undefined
      : `${nc.quantity.toLocaleString('pt-BR')}${unitName === undefined ? '' : ` ${unitName}`} não conformes`,
    lugares === '' ? undefined : lugares,
  ].filter((parte): parte is string => parte !== undefined);

  return (
    <li className="flex flex-col gap-2 rounded-md bg-(--color-surface-muted) px-3 py-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm">
            <span className="font-medium">NC nº {nc.number}</span> — {nc.description}
          </p>
          <p className="text-xs text-(--color-content-muted)">
            {nc.itemLabel} · {detalhes.join(' · ')}
          </p>
        </div>
        {onResolve !== undefined && !aberto && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={disabled}
            onClick={() => setAberto(true)}
          >
            Encerrar
          </Button>
        )}
      </div>
      {onResolve !== undefined && aberto && (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setGravando(true);
            void onResolve(nc, solution).then((encerrada) => {
              setGravando(false);
              if (encerrada) setAberto(false);
            });
          }}
        >
          <label htmlFor={`${id}-solucao`} className="text-xs text-(--color-content-muted)">
            Solução aplicada na NC nº {nc.number}
          </label>
          <Textarea
            id={`${id}-solucao`}
            rows={2}
            maxLength={MAX_NON_CONFORMITY_SOLUTION}
            placeholder="O que foi refeito."
            value={solution}
            disabled={disabled || gravando}
            onChange={(event) => setSolution(event.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              size="sm"
              variant="primary"
              disabled={
                disabled || gravando || solution.trim().length < MIN_NON_CONFORMITY_SOLUTION
              }
            >
              {gravando ? 'Encerrando…' : 'Encerrar com a solução'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={gravando}
              onClick={() => {
                setAberto(false);
                setSolution('');
              }}
            >
              Voltar
            </Button>
          </div>
        </form>
      )}
    </li>
  );
}
